import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { TrendingUp, Star, ClipboardCheck, Plus, X, AlertTriangle, CheckCircle2, Target, MessageSquare, Calendar, ChevronRight, ChevronDown, Lightbulb, Settings, Pencil, Trash2, Search, Save, Users as UsersIcon, Layers } from "lucide-react";

const sevBadge = (status) => {
  const m = {
    pending_self: "sw-badge-pending",
    pending_manager: "sw-badge-pending",
    completed: "sw-badge-approved",
    active: "sw-badge-gold",
    extended: "sw-badge-pending",
    successful: "sw-badge-approved",
    failed: "sw-badge-rejected",
    cancelled: "sw-badge-neutral",
    closed: "sw-badge-neutral",
    draft: "sw-badge-neutral",
  };
  return <span className={`sw-badge ${m[status] || "sw-badge-neutral"}`}>{status?.replace(/_/g, " ")}</span>;
};

const Tab = ({ active, onClick, label, testid, count }) => (
  <button data-testid={testid} onClick={onClick} className={`px-4 py-2 rounded-[12px] text-[13px] font-medium ${active ? "bg-[#e2e8f0] text-[#475569]" : "text-[#747474] hover:bg-[#f6f6f6]"}`}>
    {label} {count !== undefined && <span className="ml-1 opacity-60">({count})</span>}
  </button>
);

// ============ Evaluation modal ============
function EvalModal({ evalDoc, mode, onClose, onSaved }) {
  const { t } = useI18n();
  const [competencies, setCompetencies] = useState([]);
  const [scores, setScores] = useState({});
  const [overallComment, setOverallComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cycleCompetencies = async () => {
      const cycle = (await api.get("/eval-cycles")).data.find((c) => c.id === evalDoc.cycle_id);
      if (!cycle) return;
      const all = (await api.get("/competencies")).data;
      const filtered = all.filter((c) => cycle.competency_ids.includes(c.id));
      setCompetencies(filtered);
    };
    cycleCompetencies();
  }, [evalDoc.cycle_id]);

  const setScore = (cid, score) => setScores({ ...scores, [cid]: { ...(scores[cid] || {}), score } });
  const setComment = (cid, comment) => setScores({ ...scores, [cid]: { ...(scores[cid] || {}), comment } });

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        scores: competencies.map((c) => ({
          competency_id: c.id,
          score: scores[c.id]?.score || 3,
          comment: scores[c.id]?.comment || null,
        })),
        overall_comment: overallComment || null,
      };
      if (mode === "self") await api.post(`/evaluations/${evalDoc.id}/submit-self`, payload);
      else await api.post(`/evaluations/${evalDoc.id}/submit-manager`, payload);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[760px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{mode === "self" ? "Self-evaluation" : "Manager evaluation"} · {evalDoc.cycle_name}</div>
            <h3 className="sw-heading text-[20px] mt-1">{evalDoc.employee_name}</h3>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {mode === "manager" && evalDoc.self_evaluation && (
          <div className="sw-card !p-3 mb-4 bg-[#e2e8f0]/40 border-[#475569]">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#475569]"><Star size={11} className="inline mr-1" /> {t("perf.self_ref")}</div>
              <div className="text-[12px]"><span className="text-[#747474]">Avg:</span> <span className="font-semibold text-[#475569]">{evalDoc.self_evaluation.average_score}/5</span></div>
            </div>
            {evalDoc.self_evaluation.overall_comment && <div className="text-[12px] italic text-[#323232] mb-1">&ldquo;{evalDoc.self_evaluation.overall_comment}&rdquo;</div>}
            <div className="text-[10px] text-[#747474]">{t("perf.submitted_compare", { date: evalDoc.self_evaluation.submitted_at?.slice(0, 10) })}</div>
          </div>
        )}
        <div className="flex flex-col gap-3 mb-4">
          {competencies.map((c) => {
            const selfScore = evalDoc.self_evaluation?.scores?.find((s) => s.competency_id === c.id);
            return (
              <div key={c.id} className="p-3 rounded-[12px] bg-[#f6f6f6] border border-[#eeeded]">
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#475569]">{c.category}</div>
                    <div className="font-medium text-[14px] text-[#323232]">{c.name}</div>
                    <div className="text-[11px] text-[#747474] italic mt-0.5">{c.description}</div>
                  </div>
                  {mode === "manager" && selfScore && (
                    <div data-testid={`self-score-ref-${c.id}`} className="shrink-0 text-right">
                      <div className="text-[9px] uppercase tracking-wider text-[#475569]">Self</div>
                      <div className="text-[18px] font-bold text-[#475569]">{selfScore.score}<span className="text-[10px] text-[#747474]">/5</span></div>
                    </div>
                  )}
                </div>
                {mode === "manager" && selfScore?.comment && (
                  <div className="text-[11px] italic text-[#747474] mb-2 pl-2 border-l-2 border-[#475569]">&ldquo;{selfScore.comment}&rdquo;</div>
                )}
                <div className="flex gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setScore(c.id, n)} data-testid={`score-${c.id}-${n}`} className={`w-9 h-9 rounded-[10px] text-[13px] font-semibold ${scores[c.id]?.score === n ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474] hover:border-[#475569]"}`}>{n}</button>
                  ))}
                  <span className="text-[10px] text-[#747474] ml-2 self-end">{mode === "manager" ? t("perf.your_rating") : t("perf.rating_scale")}</span>
                </div>
                <input placeholder="Comment (optional)" value={scores[c.id]?.comment || ""} onChange={(e) => setComment(c.id, e.target.value)} className="sw-input !text-[12px] !py-1.5" />
              </div>
            );
          })}
        </div>
        <div className="mb-4">
          <label className="sw-label">Overall comment</label>
          <textarea rows={3} value={overallComment} onChange={(e) => setOverallComment(e.target.value)} className="sw-input" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="submit-eval-btn" onClick={submit} disabled={saving || competencies.length === 0} className="sw-btn-primary">{saving ? "..." : "Submit"}</button>
        </div>
      </div>
    </div>
  );
}

// ============ Evaluation detail (read-only view of completed) ============
function EvalDetailModal({ evalDoc, onClose }) {
  const { t } = useI18n();
  const [competencies, setCompetencies] = useState([]);
  useEffect(() => {
    api.get("/competencies").then((r) => setCompetencies(r.data));
  }, []);
  const compName = (id) => competencies.find((c) => c.id === id)?.name || id;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[760px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{evalDoc.cycle_name}</div>
            <h3 className="sw-heading text-[22px] mt-1">{evalDoc.employee_name}</h3>
            {evalDoc.summary_score && <div className="text-[18px] mt-2"><span className="text-[#747474] text-[12px]">Summary score:</span> <span className="font-bold text-[#475569]">{evalDoc.summary_score}</span> / 5</div>}
          </div>
          <div className="flex items-center gap-2">{sevBadge(evalDoc.status)}<button onClick={onClose}><X size={20} /></button></div>
        </div>

        {["self_evaluation", "manager_evaluation"].map((key) => {
          const evalu = evalDoc[key];
          if (!evalu) return null;
          return (
            <div key={key} className="sw-card !p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-[#475569]">{key === "self_evaluation" ? "Self" : "Manager"}</div>
                <div className="text-[13px]"><span className="text-[#747474]">Avg:</span> <span className="font-semibold">{evalu.average_score}</span></div>
              </div>
              <div className="flex flex-col gap-1">
                {evalu.scores?.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-[12px] py-1 border-b border-[#f6f6f6] last:border-0">
                    <span className="text-[#323232]">{compName(s.competency_id)}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#475569]">{s.score}/5</span>
                      {s.comment && <span className="text-[11px] text-[#747474] italic max-w-[300px] truncate">&ldquo;{s.comment}&rdquo;</span>}
                    </div>
                  </div>
                ))}
              </div>
              {evalu.overall_comment && <div className="mt-2 text-[12px] text-[#323232] italic">&ldquo;{evalu.overall_comment}&rdquo;</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Evaluations Tab ============
function EvaluationsTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [evals, setEvals] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [scope, setScope] = useState("mine");  // mine | to_evaluate | all (hr)
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState("self");
  const [detail, setDetail] = useState(null);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [addingToCycle, setAddingToCycle] = useState(null); // cycle object → opens AddPeopleToCycleModal

  const load = async () => {
    const params = new URLSearchParams();
    if (scope === "mine") params.set("mine", "true");
    if (scope === "to_evaluate") params.set("to_evaluate", "true");
    if (scope === "team") params.set("team", "true");
    if (selectedCycleId) params.set("cycle_id", selectedCycleId);
    const [e, c] = await Promise.all([api.get(`/evaluations?${params.toString()}`), api.get("/eval-cycles")]);
    setEvals(e.data);
    setCycles(c.data);
  };
  useEffect(() => { load(); }, [scope, selectedCycleId]); // eslint-disable-line

  const canManage = ["hr", "admin"].includes(user?.role);

  const openEval = (e) => {
    if (e.status === "pending_self" && e.employee_id === user.id) {
      setEditing(e); setEditMode("self");
    } else if (e.status === "pending_manager" && (e.evaluator_id === user.id || canManage)) {
      setEditing(e); setEditMode("manager");
    } else {
      setDetail(e);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setScope("mine")} data-testid="eval-scope-mine" className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "mine" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>My evaluations</button>
          {["manager", "hr", "ceo", "admin"].includes(user?.role) && <button onClick={() => setScope("to_evaluate")} data-testid="eval-scope-to-eval" className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "to_evaluate" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("perf.tab_pending")}</button>}
          {["manager", "hr", "ceo", "admin"].includes(user?.role) && <button onClick={() => setScope("team")} data-testid="eval-scope-team" className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "team" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("perf.tab_team")}</button>}
          {canManage && <button onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-[10px] text-[12px] ${scope === "all" ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{t("perf.tab_all")}</button>}
        </div>
        {canManage && <button data-testid="new-cycle-btn" onClick={() => setShowCycleModal(true)} className="sw-btn-primary"><Plus size={14} /> {t("perf.new_cycle")}</button>}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider text-[#475569]">Cycles · click to filter evaluations</div>
          {selectedCycleId && <button data-testid="clear-cycle-filter" onClick={() => setSelectedCycleId(null)} className="text-[11px] text-[#475569] hover:underline">✕ Clear filter</button>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {cycles.map((c) => {
            const sel = selectedCycleId === c.id;
            return (
              <button key={c.id} data-testid={`cycle-card-${c.id}`} onClick={() => setSelectedCycleId(sel ? null : c.id)} className={`sw-card !p-3 text-left hover:border-[#475569] ${sel ? "border-[#475569] border-2 bg-[#e2e8f0]/50" : ""}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#475569]">{c.period}{sel && " · FILTERED"}</div>
                    <div className="font-medium text-[14px]">{c.name}</div>
                    <div className="text-[11px] text-[#747474]">Due {c.due_date} · {c.competency_ids?.length} competencies · {c.evaluations_created || 0} eval(s)</div>
                  </div>
                  <div className="flex flex-col items-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {sevBadge(c.status)}
                    {canManage && c.status === "draft" && <button data-testid={`activate-cycle-${c.id}`} onClick={async (e) => { e.stopPropagation(); await api.post(`/eval-cycles/${c.id}/activate`); load(); }} className="sw-btn-secondary !text-[11px] !px-2 !py-0.5">Activate</button>}
                    {canManage && c.status === "active" && <button onClick={async (e) => { e.stopPropagation(); await api.post(`/eval-cycles/${c.id}/close`); load(); }} className="sw-btn-secondary !text-[11px] !px-2 !py-0.5">{t("common.close")}</button>}
                    {canManage && c.status !== "closed" && (
                      <button
                        data-testid={`add-people-cycle-${c.id}`}
                        onClick={(e) => { e.stopPropagation(); setAddingToCycle(c); }}
                        className="sw-btn-secondary !text-[11px] !px-2 !py-0.5"
                      ><Plus size={11} /> Adicionar pessoas</button>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2">{selectedCycleId ? "Evaluations in this cycle" : "Evaluations"} ({evals.length})</div>
        {evals.length === 0 ? (
          <div className="sw-card text-center py-12 text-[#747474]">No evaluations match the current filter. {selectedCycleId && <button onClick={() => setSelectedCycleId(null)} className="ml-2 text-[#475569] hover:underline">{t("perf.clear_filter")}</button>}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {evals.map((e) => (
              <div key={e.id} className="relative">
                <button data-testid={`eval-card-${e.id}`} onClick={() => openEval(e)} className="sw-card text-left hover:border-[#475569] w-full">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-[#475569]">{e.cycle_name}</div>
                      <div className="font-medium text-[15px] mt-1">{e.employee_name}</div>
                      <div className="text-[11px] text-[#747474]">Evaluator: {e.evaluator_name || "—"} · Due {e.due_date}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {sevBadge(e.status)}
                      {e.summary_score && <span className="text-[14px] font-bold text-[#475569]">{e.summary_score}/5</span>}
                    </div>
                  </div>
                  {e.status === "pending_self" && e.employee_id === user?.id && <div className="text-[11px] text-[#92400e] font-medium mt-1">{t("perf.click_fill_self")}</div>}
                  {e.status === "pending_manager" && (e.evaluator_id === user?.id || canManage) && <div className="text-[11px] text-[#92400e] font-medium mt-1">→ Click to fill manager evaluation</div>}
                </button>
                {canManage && (
                  <button
                    data-testid={`delete-eval-${e.id}`}
                    title={t("perf.del_eval_tooltip")}
                    onClick={async (ev) => {
                      ev.stopPropagation();
                      if (!confirm(t("perf.del_eval_confirm", { name: e.employee_name }))) return;
                      try {
                        await api.delete(`/evaluations/${e.id}`);
                        toast.success(t("perf.eval_deleted"));
                        load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Falha ao excluir.");
                      }
                    }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-[#881a27] bg-white/80 hover:bg-[#fef1f3] rounded-[8px] border border-transparent hover:border-[#f5e8d5]"
                  ><Trash2 size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <EvalModal evalDoc={editing} mode={editMode} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <EvalDetailModal evalDoc={detail} onClose={() => setDetail(null)} />}
      {showCycleModal && <CycleModal onClose={() => setShowCycleModal(false)} onSaved={() => { setShowCycleModal(false); load(); }} />}
      {addingToCycle && <AddPeopleToCycleModal cycle={addingToCycle} onClose={() => setAddingToCycle(null)} onSaved={() => { setAddingToCycle(null); load(); }} />}
    </div>
  );
}

/**
 * Iter 46b — Modal to add extra people to an existing cycle.
 * Uses the same DeptTreePicker as the CycleModal step 2 so HR keeps the
 * exact same UX; excludes anyone who is already in the cycle so we can't
 * accidentally duplicate.
 */
function AddPeopleToCycleModal({ cycle, onClose, onSaved }) {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [existingIds, setExistingIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/users"),
      api.get("/departments"),
      api.get(`/evaluations?cycle_id=${cycle.id}`),
    ]).then(([u, d, e]) => {
      setUsers(u.data);
      setDepts(d.data);
      setExistingIds(new Set(e.data.map((x) => x.employee_id)));
    });
  }, [cycle.id]);

  // Hide people already in the cycle from the picker so HR can't re-add them.
  const pickableUsers = useMemo(() => users.filter((u) => !existingIds.has(u.id)), [users, existingIds]);

  const save = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      const r = await api.post(`/eval-cycles/${cycle.id}/add-users`, { user_ids: selectedIds });
      const { added = 0, skipped = 0 } = r.data || {};
      toast.success(t("perf.evals_created", { added }) + (skipped > 0 ? ` · ${t("perf.evals_skipped", { skipped })}` : "") + ".");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao adicionar pessoas.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-people-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("perf.add_people")}</div>
            <h3 className="sw-heading text-[20px] mt-0.5">{cycle.name}</h3>
            <div className="text-[11.5px] text-[#94a3b8]">{existingIds.size} pessoa(s) já no ciclo — ficam ocultas na lista abaixo pra evitar duplicatas.</div>
          </div>
          <button onClick={onClose} data-testid="close-add-people"><X size={20} /></button>
        </div>
        <DeptTreePicker
          users={pickableUsers}
          departments={depts}
          value={selectedIds}
          onChange={setSelectedIds}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button
            onClick={save}
            disabled={saving || selectedIds.length === 0}
            className="sw-btn-primary"
            data-testid="save-add-people-btn"
          >{saving ? "Adicionando…" : `Adicionar ${selectedIds.length} pessoa${selectedIds.length === 1 ? "" : "s"}`}</button>
        </div>
      </div>
    </div>
  );
}

function CycleModal({ onClose, onSaved }) {
  const { t } = useI18n();
  const [competencies, setCompetencies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [form, setForm] = useState({ name: "", period: "", competency_ids: [], target_user_ids: [], due_date: "" });
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1); // 1: cycle info + competencies · 2: audience picker

  useEffect(() => {
    Promise.all([
      api.get("/competencies"),
      api.get("/eval-groups"),
      api.get("/users"),
      api.get("/departments"),
    ]).then(([c, g, u, d]) => { setCompetencies(c.data); setGroups(g.data); setUsers(u.data); setDepts(d.data); });
  }, []);

  const groupById = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  // Legacy competencies (created before Iter 46) only have `category` — bind
  // them to a synthetic group so they don't disappear from the picker.
  const compsByGroup = useMemo(() => {
    const buckets = {};
    competencies.forEach((c) => {
      let gid = c.group_id;
      if (!gid && c.category) {
        // find seeded group with matching legacy_category
        const seeded = groups.find((g) => g.legacy_category === c.category);
        gid = seeded?.id || "unassigned";
      } else if (!gid) {
        gid = "unassigned";
      }
      buckets[gid] = buckets[gid] || [];
      buckets[gid].push(c);
    });
    return buckets;
  }, [competencies, groups]);

  const toggleComp = (id) => setForm({ ...form, competency_ids: form.competency_ids.includes(id) ? form.competency_ids.filter((x) => x !== id) : [...form.competency_ids, id] });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/eval-cycles", form);
      toast.success(t("perf.cycle_draft_created"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar ciclo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cycle-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">Novo ciclo de avaliação · passo {step}/2</div>
            <h3 className="sw-heading text-[20px] mt-0.5">{step === 1 ? "Ciclo & Perguntas" : "Quem vai avaliar"}</h3>
          </div>
          <button onClick={onClose} data-testid="close-cycle"><X size={20} /></button>
        </div>

        {step === 1 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
              <div className="md:col-span-2"><label className="sw-label">Nome do ciclo *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" placeholder={t("perf.cycle_name_ph")} data-testid="cycle-name" /></div>
              <div><label className="sw-label">Período *</label><input placeholder="Ex.: 2026-H1" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="sw-input" data-testid="cycle-period" /></div>
              <div><label className="sw-label">Data limite *</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="sw-input" data-testid="cycle-due" /></div>
            </div>
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2 font-semibold">{t("perf.questions_by_group")} *</div>
              <div className="flex flex-col gap-3 max-h-[340px] overflow-y-auto pr-1">
                {groups.map((g) => {
                  const items = compsByGroup[g.id] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={g.id} className="border border-[#eeeded] rounded-[12px] p-3">
                      <div className="text-[12px] font-semibold text-[#0f172a] mb-2">{g.name} <span className="opacity-60 font-normal">({items.length})</span></div>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((c) => (
                          <button type="button" key={c.id} onClick={() => toggleComp(c.id)} data-testid={`comp-toggle-${c.id}`} className={`px-2.5 py-1 rounded-[8px] text-[11.5px] border transition-all ${form.competency_ids.includes(c.id) ? "bg-[#475569] text-white border-[#475569]" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}>
                            {c.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {(compsByGroup.unassigned || []).length > 0 && (
                  <div className="border border-dashed border-[#e2e8f0] rounded-[12px] p-3">
                    <div className="text-[12px] font-semibold text-[#94a3b8] mb-2">Sem grupo ({(compsByGroup.unassigned || []).length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(compsByGroup.unassigned || []).map((c) => (
                        <button type="button" key={c.id} onClick={() => toggleComp(c.id)} className={`px-2.5 py-1 rounded-[8px] text-[11.5px] border ${form.competency_ids.includes(c.id) ? "bg-[#475569] text-white border-[#475569]" : "bg-white border-[#e2e8f0] text-[#475569]"}`}>{c.name}</button>
                      ))}
                    </div>
                  </div>
                )}
                {groups.length === 0 && (
                  <div className="text-[12.5px] text-[#94a3b8] italic">{t("perf.no_groups_yet")} <b>{t("perf.tab_config")} → {t("perf.groups_title")}</b>.</div>
                )}
              </div>
              <div className="text-[11.5px] text-[#94a3b8] mt-2">{t("perf.questions_selected", { n: form.competency_ids.length })}</div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
              <button onClick={() => setStep(2)} disabled={!form.name || form.competency_ids.length === 0 || !form.due_date} className="sw-btn-primary" data-testid="cycle-next-step">{t("common.next")} <ChevronRight size={13} /></button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <DeptTreePicker
              users={users}
              departments={depts}
              value={form.target_user_ids}
              onChange={(ids) => setForm({ ...form, target_user_ids: ids })}
            />
            <div className="flex justify-between gap-2 mt-4">
              <button onClick={() => setStep(1)} className="sw-btn-secondary">Voltar</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
                <button data-testid="save-cycle-btn" onClick={save} disabled={saving || form.target_user_ids.length === 0} className="sw-btn-primary">
                  <Save size={13} /> {saving ? "Salvando…" : `Criar rascunho (${form.target_user_ids.length} pessoa${form.target_user_ids.length === 1 ? "" : "s"})`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Iter 46 — Expandable dept tree with per-employee checkboxes.
 * - Filter by name/email OR by role/job title.
 * - Checking a department implies "all currently-visible team members in this dept";
 *   partial-selection shows a mixed (–) indicator.
 * - `value` is a flat array of user_ids; parent receives the array unchanged.
 */
function DeptTreePicker({ users, departments, value, onChange }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [roleFilters, setRoleFilters] = useState(new Set());
  const [openDepts, setOpenDepts] = useState({});

  const selected = new Set(value);
  const toggleRole = (r) => {
    const next = new Set(roleFilters);
    next.has(r) ? next.delete(r) : next.add(r);
    setRoleFilters(next);
  };

  // Filter users by role + search text
  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (u.role === "admin" && !u.department_id) return false; // hide system admin
      if (roleFilters.size > 0 && !roleFilters.has(u.role)) return false;
      if (!needle) return true;
      const hay = [u.name, u.email, u.job_title].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q, roleFilters]);

  const usersByDept = useMemo(() => {
    const m = {};
    filteredUsers.forEach((u) => {
      const key = u.department_id || "UNASSIGNED";
      m[key] = m[key] || [];
      m[key].push(u);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    return m;
  }, [filteredUsers]);

  // Auto-expand any dept whose set of visible employees matches a search
  useEffect(() => {
    if (q.trim()) {
      const next = {};
      Object.keys(usersByDept).forEach((k) => { next[k] = true; });
      setOpenDepts(next);
    }
  }, [q, usersByDept]);

  const deptStatus = (deptId) => {
    const list = usersByDept[deptId] || [];
    if (list.length === 0) return "empty";
    const inList = list.filter((u) => selected.has(u.id)).length;
    if (inList === 0) return "none";
    if (inList === list.length) return "all";
    return "partial";
  };

  const toggleDept = (deptId) => {
    const list = usersByDept[deptId] || [];
    const status = deptStatus(deptId);
    const ids = new Set(value);
    if (status === "all") list.forEach((u) => ids.delete(u.id));
    else list.forEach((u) => ids.add(u.id));
    onChange(Array.from(ids));
  };

  const toggleUser = (uid) => {
    const ids = new Set(value);
    ids.has(uid) ? ids.delete(uid) : ids.add(uid);
    onChange(Array.from(ids));
  };

  const selectAllVisible = () => {
    const ids = new Set(value);
    filteredUsers.forEach((u) => ids.add(u.id));
    onChange(Array.from(ids));
  };
  const clearAll = () => onChange([]);

  const roles = ["employee", "manager", "hr", "ceo"];
  const orderedDepts = [...departments].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="flex flex-col gap-3" data-testid="dept-tree-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("perf.search_ph")} className="sw-input !pl-8 !text-[12.5px]" data-testid="tree-search" />
        </div>
        <button type="button" onClick={selectAllVisible} className="sw-btn-secondary !text-[11.5px]" data-testid="tree-select-visible">{t("perf.mark_visible")}</button>
        <button type="button" onClick={clearAll} className="sw-btn-secondary !text-[11.5px]" data-testid="tree-clear">Limpar</button>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[11px] text-[#94a3b8]">{t("perf.role_label")}</span>
        {roles.map((r) => (
          <button
            type="button"
            key={r}
            onClick={() => toggleRole(r)}
            data-testid={`tree-role-${r}`}
            className={`px-2 py-0.5 rounded-full text-[11px] border capitalize ${roleFilters.has(r) ? "bg-[#475569] text-white border-[#475569]" : "bg-white border-[#e2e8f0] text-[#475569]"}`}
          >{r}</button>
        ))}
        {roleFilters.size > 0 && (
          <button type="button" onClick={() => setRoleFilters(new Set())} className="text-[11px] text-[#475569] hover:underline">limpar filtro</button>
        )}
      </div>

      <div className="border border-[#eeeded] rounded-[12px] max-h-[360px] overflow-y-auto bg-white">
        {orderedDepts.map((d) => {
          const list = usersByDept[d.id] || [];
          if (list.length === 0 && (q.trim() || roleFilters.size > 0)) return null;
          const status = deptStatus(d.id);
          const isOpen = openDepts[d.id];
          const inList = list.filter((u) => selected.has(u.id)).length;
          return (
            <div key={d.id} className="border-b border-[#f1f5f9] last:border-b-0">
              <div className="flex items-center gap-2 px-3 py-2 hover:bg-[#f8fafc]">
                <button
                  type="button"
                  onClick={() => setOpenDepts({ ...openDepts, [d.id]: !isOpen })}
                  className="w-5 h-5 flex items-center justify-center text-[#475569] hover:bg-[#e2e8f0] rounded-[6px]"
                  data-testid={`dept-toggle-${d.id}`}
                >{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</button>
                <input
                  type="checkbox"
                  checked={status === "all"}
                  ref={(el) => { if (el) el.indeterminate = status === "partial"; }}
                  onChange={() => toggleDept(d.id)}
                  className="w-4 h-4 accent-[#475569] cursor-pointer"
                  data-testid={`dept-check-${d.id}`}
                  disabled={list.length === 0}
                />
                <div className="flex-1 flex items-center justify-between">
                  <div className="text-[13px] font-medium text-[#0f172a]">{d.name}</div>
                  <div className="text-[11px] text-[#94a3b8]">{inList}/{list.length} selecionados</div>
                </div>
              </div>
              {isOpen && (
                <div className="pl-11 pr-3 pb-2">
                  {list.length === 0 ? (
                    <div className="text-[11.5px] text-[#94a3b8] italic py-1">{t("perf.no_visible")}</div>
                  ) : (
                    list.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-[#f8fafc] rounded-[6px] px-2">
                        <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleUser(u.id)} className="w-3.5 h-3.5 accent-[#475569]" data-testid={`user-check-${u.id}`} />
                        <div className="flex-1 text-[12.5px]">
                          <span className="text-[#0f172a]">{u.name}</span>
                          <span className="text-[#94a3b8] ml-1.5">· <span className="capitalize">{u.role}</span>{u.job_title ? ` · ${u.job_title}` : ""}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
        {(usersByDept.UNASSIGNED || []).length > 0 && (
          <div className="border-t border-dashed border-[#e2e8f0] px-3 py-2 bg-[#fef8f2]">
            <div className="text-[11px] font-semibold text-[#7c5a1e] mb-1">{t("perf.no_dept")}</div>
            {(usersByDept.UNASSIGNED || []).map((u) => (
              <label key={u.id} className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleUser(u.id)} className="w-3.5 h-3.5 accent-[#475569]" />
                <span className="text-[12.5px]">{u.name} <span className="text-[#94a3b8]">· <span className="capitalize">{u.role}</span></span></span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="text-[12px] text-[#475569] font-medium" data-testid="tree-selected-count">
        {value.length} integrante{value.length === 1 ? "" : "es"} selecionado{value.length === 1 ? "" : "s"} · {filteredUsers.length} visívei{filteredUsers.length === 1 ? "l" : "s"}
      </div>
    </div>
  );
}

/**
 * Iter 46 — HR/Admin Configuration tab.
 * Two side-by-side cards:
 *  - Grupos: CRUD list, drag-to-reorder-by-order-field via up/down arrows.
 *  - Perguntas: grouped by group_id, add/edit/remove; each question binds to one group.
 */
function ConfigTab() {
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null); // null | {} for new | existing group
  const [editingComp, setEditingComp] = useState(null);

  const load = async () => {
    const [g, c] = await Promise.all([api.get("/eval-groups"), api.get("/competencies")]);
    setGroups(g.data);
    setCompetencies(c.data);
  };
  useEffect(() => { load(); }, []);

  const compsByGroup = useMemo(() => {
    const m = {};
    competencies.forEach((c) => {
      let gid = c.group_id;
      if (!gid && c.category) {
        const seed = groups.find((g) => g.legacy_category === c.category);
        gid = seed?.id || "unassigned";
      } else if (!gid) {
        gid = "unassigned";
      }
      m[gid] = m[gid] || [];
      m[gid].push(c);
    });
    return m;
  }, [competencies, groups]);

  const removeGroup = async (g) => {
    const count = (compsByGroup[g.id] || []).length;
    const msg = count > 0
      ? t("perf.remove_group_confirm", { name: g.name, count })
      : t("common.delete_named", { title: g.name });
    if (!confirm(msg)) return;
    await api.delete(`/eval-groups/${g.id}`);
    toast.success(t("perf.group_removed"));
    load();
  };

  const removeComp = async (c) => {
    if (!confirm(t("perf.remove_q_confirm", { name: c.name }))) return;
    await api.delete(`/competencies/${c.id}`);
    toast.success(t("perf.q_removed"));
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* GROUPS */}
      <div className="sw-card !p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-[#475569]" />
            <h3 className="sw-heading text-[16px]">{t("perf.groups_title")}</h3>
          </div>
          <button onClick={() => setEditingGroup({})} className="sw-btn-primary !text-[12px]" data-testid="new-group-btn"><Plus size={12} />  {t("perf.new_group")}</button>
        </div>
        <p className="text-[12px] text-[#94a3b8] mb-3">{t("perf.groups_hint")}</p>
        <div className="flex flex-col gap-2">
          {groups.length === 0 && <div className="text-[12.5px] text-[#94a3b8] italic py-3">{t("perf.no_groups")}</div>}
          {groups.map((g) => (
            <div key={g.id} className="border border-[#eeeded] rounded-[10px] px-3 py-2.5 flex items-center gap-3 hover:bg-[#f8fafc]" data-testid={`group-row-${g.id}`}>
              <div className="w-6 h-6 rounded-full bg-[#eef2f7] text-[#475569] flex items-center justify-center text-[11px] font-semibold">{g.order || "-"}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#0f172a]">{g.name}</div>
                {g.description && <div className="text-[11.5px] text-[#94a3b8]">{g.description}</div>}
                <div className="text-[10.5px] text-[#94a3b8] mt-0.5">{t("perf.questions_n", { n: (compsByGroup[g.id] || []).length })}</div>
              </div>
              <button onClick={() => setEditingGroup(g)} className="text-[#475569] hover:bg-[#e2e8f0] p-1 rounded-[6px]" data-testid={`edit-group-${g.id}`}><Pencil size={13} /></button>
              <button onClick={() => removeGroup(g)} className="text-[#881a27] hover:bg-[#fef1f3] p-1 rounded-[6px]" data-testid={`delete-group-${g.id}`}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* QUESTIONS */}
      <div className="sw-card !p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-[#475569]" />
            <h3 className="sw-heading text-[16px]">{t("perf.questions_by_group")}</h3>
          </div>
          <button onClick={() => setEditingComp({})} disabled={groups.length === 0} className="sw-btn-primary !text-[12px]" data-testid="new-comp-btn"><Plus size={12} />  {t("perf.new_question")}</button>
        </div>
        <p className="text-[12px] text-[#94a3b8] mb-3">{t("perf.questions_hint")}</p>
        <div className="flex flex-col gap-3 max-h-[520px] overflow-y-auto pr-1">
          {groups.map((g) => {
            const items = compsByGroup[g.id] || [];
            return (
              <div key={g.id}>
                <div className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold mb-1.5">{g.name} <span className="opacity-60 font-normal">({items.length})</span></div>
                {items.length === 0 && <div className="text-[11.5px] text-[#94a3b8] italic pl-2 mb-1">{t("perf.no_questions")}</div>}
                {items.map((c) => (
                  <div key={c.id} className="border border-[#eeeded] rounded-[10px] px-3 py-2 flex items-center gap-2 mb-1.5" data-testid={`comp-row-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#0f172a]">{c.name}</div>
                      {c.description && <div className="text-[11.5px] text-[#94a3b8] truncate">{c.description}</div>}
                    </div>
                    <button onClick={() => setEditingComp(c)} className="text-[#475569] hover:bg-[#e2e8f0] p-1 rounded-[6px]" data-testid={`edit-comp-${c.id}`}><Pencil size={12} /></button>
                    <button onClick={() => removeComp(c)} className="text-[#881a27] hover:bg-[#fef1f3] p-1 rounded-[6px]" data-testid={`delete-comp-${c.id}`}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            );
          })}
          {(compsByGroup.unassigned || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-1.5">Sem grupo ({(compsByGroup.unassigned || []).length})</div>
              {compsByGroup.unassigned.map((c) => (
                <div key={c.id} className="border border-dashed border-[#e2e8f0] rounded-[10px] px-3 py-2 flex items-center gap-2 mb-1.5">
                  <div className="flex-1 text-[13px]">{c.name}</div>
                  <button onClick={() => setEditingComp(c)} className="text-[#475569] hover:bg-[#e2e8f0] p-1 rounded-[6px]"><Pencil size={12} /></button>
                  <button onClick={() => removeComp(c)} className="text-[#881a27] hover:bg-[#fef1f3] p-1 rounded-[6px]"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editingGroup !== null && <GroupModal group={editingGroup} onClose={() => setEditingGroup(null)} onSaved={() => { setEditingGroup(null); load(); }} />}
      {editingComp !== null && <CompModal comp={editingComp} groups={groups} onClose={() => setEditingComp(null)} onSaved={() => { setEditingComp(null); load(); }} />}
    </div>
  );
}

function GroupModal({ group, onClose, onSaved }) {
  const { t } = useI18n();
  const isNew = !group.id;
  const [form, setForm] = useState({ name: group.name || "", description: group.description || "", order: group.order || (isNew ? 99 : 0) });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) await api.post("/eval-groups", form);
      else await api.patch(`/eval-groups/${group.id}`, form);
      toast.success(isNew ? t("perf.group_created") : t("perf.group_updated"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-[16px] max-w-[440px] w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="group-modal">
        <div className="flex justify-between items-start mb-3">
          <h3 className="sw-heading text-[16px]">{isNew ? t("perf.new_group") : t("perf.edit_group")}</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex flex-col gap-3">
          <div><label className="sw-label">{t("common.name")} *</label><input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" placeholder={t("perf.group_name_ph")} data-testid="group-name" /></div>
          <div><label className="sw-label">{t("common.description")}</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" placeholder={t("perf.group_desc_ph")} /></div>
          <div><label className="sw-label">{t("common.order")}</label><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="sw-input" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button type="submit" disabled={saving || !form.name} className="sw-btn-primary" data-testid="save-group-btn">{saving ? "..." : t("common.save")}</button>
        </div>
      </form>
    </div>
  );
}

function CompModal({ comp, groups, onClose, onSaved }) {
  const { t } = useI18n();
  const isNew = !comp.id;
  const [form, setForm] = useState({
    name: comp.name || "",
    description: comp.description || "",
    group_id: comp.group_id || (groups[0]?.id || ""),
    applies_to_roles: comp.applies_to_roles || [],
  });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) await api.post("/competencies", form);
      else await api.patch(`/competencies/${comp.id}`, form);
      toast.success(isNew ? t("perf.q_created") : t("perf.q_updated"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };
  const roles = ["employee", "manager", "hr", "ceo"];
  const toggleRole = (r) => setForm({ ...form, applies_to_roles: form.applies_to_roles.includes(r) ? form.applies_to_roles.filter((x) => x !== r) : [...form.applies_to_roles, r] });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="bg-white rounded-[16px] max-w-[520px] w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="comp-modal">
        <div className="flex justify-between items-start mb-3">
          <h3 className="sw-heading text-[16px]">{isNew ? t("perf.new_question") : t("perf.edit_question")}</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex flex-col gap-3">
          <div><label className="sw-label">{t("perf.q_comp_label")} *</label><input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" placeholder={t("perf.q_ph")} data-testid="comp-name" /></div>
          <div><label className="sw-label">{t("perf.groups_title")} *</label>
            <select value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })} className="sw-input" data-testid="comp-group">
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div><label className="sw-label">{t("perf.q_help")}</label><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" placeholder={t("perf.q_help_ph")} /></div>
          <div>
            <label className="sw-label">{t("perf.applies_roles")}</label>
            <div className="flex gap-1.5 flex-wrap">
              {roles.map((r) => (
                <button type="button" key={r} onClick={() => toggleRole(r)} className={`px-2 py-0.5 rounded-full text-[11px] border capitalize ${form.applies_to_roles.includes(r) ? "bg-[#475569] text-white border-[#475569]" : "bg-white border-[#e2e8f0] text-[#475569]"}`}>{r}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button type="submit" disabled={saving || !form.name || !form.group_id} className="sw-btn-primary" data-testid="save-comp-btn">{saving ? "..." : t("common.save")}</button>
        </div>
      </form>
    </div>
  );
}


// ============ PIP Tab ============
function PIPTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [pips, setPips] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterStatus !== "all") params.set("status", filterStatus);
    const r = await api.get(`/pips?${params.toString()}`);
    setPips(r.data);
  };
  useEffect(() => { load(); }, [filterStatus]); // eslint-disable-line

  const canCreate = ["manager", "hr", "ceo", "admin"].includes(user?.role);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap">
          {["all", "active", "successful", "failed", "extended", "cancelled"].map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)} data-testid={`pip-filter-${s}`} className={`px-3 py-1.5 rounded-[10px] text-[11px] font-medium ${filterStatus === s ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>{s}</button>
          ))}
        </div>
        {canCreate && <button data-testid="new-pip-btn" onClick={() => setShowNew(true)} className="sw-btn-primary"><Plus size={14} /> {t("perf.new_pip")}</button>}
      </div>

      {pips.length === 0 ? (
        <div className="sw-card text-center py-12 text-[#747474]">{t("perf.no_pips")}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {pips.map((p) => {
            const goalsDone = p.goals?.filter((g) => g.status === "completed").length || 0;
            const goalsTotal = p.goals?.length || 0;
            return (
              <button key={p.id} data-testid={`pip-card-${p.id}`} onClick={() => setDetail(p)} className="sw-card text-left hover:border-[#475569]">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#475569]">PIP · {p.employee_role}</div>
                    <div className="font-medium text-[15px] mt-1">{p.employee_name}</div>
                    <div className="text-[11px] text-[#747474] line-clamp-2 mt-1 italic">&ldquo;{p.reason}&rdquo;</div>
                  </div>
                  {sevBadge(p.status)}
                </div>
                <div className="flex items-center justify-between mt-3 text-[11px]">
                  <span className="text-[#747474]"><Calendar size={10} className="inline mr-1" />{p.start_date} → {p.end_date}</span>
                  <span className="text-[#747474]"><Target size={10} className="inline mr-1" />{goalsDone}/{goalsTotal} goals · {p.follow_ups?.length || 0} follow-ups</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showNew && <NewPIPModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detail && <PIPDetailModal pip={detail} user={user} onClose={() => setDetail(null)} onUpdated={async () => { await load(); const fresh = (await api.get(`/pips/${detail.id}`)).data; setDetail(fresh); }} />}
    </div>
  );
}

function NewPIPModal({ onClose, onSaved }) {
  const { t } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", reason: "", start_date: "", end_date: "", goals: [{ description: "", target: "", success_criteria: "", due_date: "" }] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/users").then((r) => setEmployees(r.data.filter((u) => u.active !== false && u.role !== "admin")));
  }, []);
  const addGoal = () => setForm({ ...form, goals: [...form.goals, { description: "", target: "", success_criteria: "", due_date: "" }] });
  const updGoal = (i, patch) => setForm({ ...form, goals: form.goals.map((g, idx) => idx === i ? { ...g, ...patch } : g) });
  const removeGoal = (i) => setForm({ ...form, goals: form.goals.filter((_, idx) => idx !== i) });
  const save = async () => {
    setSaving(true);
    try { await api.post("/pips", form); onSaved(); }
    catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[720px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4"><h3 className="sw-heading text-[18px]">{t("perf.new_pip")}</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2"><label className="sw-label">Employee *</label>
            <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="sw-input" data-testid="pip-employee">
              <option value="">—</option>
              {employees.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="sw-label">Reason for PIP *</label><textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="sw-input" data-testid="pip-reason" /></div>
          <div><label className="sw-label">Start date *</label><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="sw-input" data-testid="pip-start" /></div>
          <div><label className="sw-label">End date *</label><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="sw-input" data-testid="pip-end" /></div>
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">SMART Goals *</div>
            <button onClick={addGoal} className="sw-btn-secondary !text-[11px] !px-2 !py-0.5"><Plus size={10} /> {t("perf.add_goal")}</button>
          </div>
          <div className="flex flex-col gap-2">
            {form.goals.map((g, i) => (
              <div key={i} className="p-3 rounded-[10px] bg-[#f6f6f6] border border-[#eeeded]">
                <div className="grid grid-cols-12 gap-2">
                  <input placeholder="Goal description" value={g.description} onChange={(e) => updGoal(i, { description: e.target.value })} className="sw-input col-span-12 !text-[12px] !py-1.5" />
                  <input placeholder="Target (e.g. ship 3 features)" value={g.target} onChange={(e) => updGoal(i, { target: e.target.value })} className="sw-input col-span-6 !text-[12px] !py-1.5" />
                  <input placeholder="Success criteria" value={g.success_criteria} onChange={(e) => updGoal(i, { success_criteria: e.target.value })} className="sw-input col-span-6 !text-[12px] !py-1.5" />
                  <input type="date" value={g.due_date} onChange={(e) => updGoal(i, { due_date: e.target.value })} className="sw-input col-span-11 !text-[12px] !py-1.5" />
                  {form.goals.length > 1 && <button onClick={() => removeGoal(i)} className="col-span-1 text-[#881a27]"><X size={14} /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="save-pip-btn" onClick={save} disabled={saving} className="sw-btn-primary">{saving ? "..." : "Create PIP"}</button>
        </div>
      </div>
    </div>
  );
}

function PIPDetailModal({ pip, user, onClose, onUpdated }) {
  const { t } = useI18n();
  const [showFU, setShowFU] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [fu, setFU] = useState({ type: "30d", notes: "", rating: "on_track", evidence_url: "" });
  const [closeForm, setCloseForm] = useState({ outcome: "successful", closing_notes: "", new_end_date: "" });
  const [working, setWorking] = useState(false);
  const canEdit = ["manager", "hr", "ceo", "admin"].includes(user?.role) && pip.status === "active";

  const addFU = async () => {
    setWorking(true);
    try { await api.post(`/pips/${pip.id}/follow-up`, fu); setShowFU(false); setFU({ type: "30d", notes: "", rating: "on_track", evidence_url: "" }); await onUpdated(); }
    catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setWorking(false); }
  };
  const closePIP = async () => {
    setWorking(true);
    try {
      const payload = { ...closeForm };
      if (payload.outcome !== "extended") delete payload.new_end_date;
      await api.post(`/pips/${pip.id}/close`, payload);
      setShowClose(false);
      await onUpdated();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setWorking(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">PIP · {pip.employee_role}</div>
            <h3 className="sw-heading text-[22px] mt-1">{pip.employee_name}</h3>
            <div className="text-[12px] text-[#747474] mt-1"><Calendar size={11} className="inline mr-1" />{pip.start_date} → {pip.end_date} · Created by {pip.created_by_name}</div>
          </div>
          <div className="flex items-center gap-2">{sevBadge(pip.status)}<button onClick={onClose}><X size={20} /></button></div>
        </div>
        <div className="sw-card !p-3 mb-4 bg-[#fef3c7] border-[#f59e0b]">
          <div className="text-[11px] uppercase tracking-wider text-[#92400e] mb-1">Reason</div>
          <div className="text-[13px] text-[#323232]">{pip.reason}</div>
        </div>
        <div className="sw-card !p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3"><Target size={11} className="inline" /> Goals</div>
          <div className="flex flex-col gap-2">
            {pip.goals?.map((g, i) => (
              <div key={i} className="p-2 rounded-[10px] bg-[#f6f6f6]">
                <div className="font-medium text-[13px]">{g.description}</div>
                <div className="text-[11px] text-[#747474] mt-1"><span className="font-semibold">Target:</span> {g.target} · <span className="font-semibold">Success:</span> {g.success_criteria} · <span className="font-semibold">Due:</span> {g.due_date}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="sw-card !p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]"><MessageSquare size={11} className="inline" /> Follow-ups ({pip.follow_ups?.length || 0})</div>
            {canEdit && <button data-testid="add-followup-btn" onClick={() => setShowFU(true)} className="sw-btn-secondary !text-[11px] !px-2 !py-1"><Plus size={10} /> {t("common.add")}</button>}
          </div>
          {pip.follow_ups?.length === 0 ? <div className="text-[12px] text-[#747474]">{t("perf.no_followups")}</div> : (
            <div className="flex flex-col gap-2">
              {pip.follow_ups?.map((f, i) => (
                <div key={i} className={`p-3 rounded-[10px] ${f.rating === "off_track" ? "bg-[#ffefef]" : f.rating === "needs_improvement" ? "bg-[#fef3c7]" : "bg-[#b8e6cd]/30"}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[12px] font-medium">{f.type.toUpperCase()} · {t(`status.${f.rating}`)}</div>
                      <div className="text-[11px] text-[#747474]">{f.by_name} · {f.at?.slice(0, 10)}</div>
                    </div>
                    {f.evidence_url && <a href={f.evidence_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#475569] underline">Evidence</a>}
                  </div>
                  <div className="text-[12px] mt-2 text-[#323232]">{f.notes}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {pip.closing_notes && (
          <div className="sw-card !p-3 mb-4 bg-[#e2e8f0]/30">
            <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-1">Closing notes · {pip.outcome}</div>
            <div className="text-[12px]">{pip.closing_notes}</div>
          </div>
        )}
        {canEdit && (
          <div className="flex justify-end">
            <button data-testid="close-pip-btn" onClick={() => setShowClose(true)} className="sw-btn-primary !bg-[#881a27] hover:!bg-[#6a1320]">{t("perf.close_pip")}</button>
          </div>
        )}
        {showFU && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowFU(false)}>
            <div className="bg-white rounded-[16px] max-w-[480px] w-full p-5" onClick={(e) => e.stopPropagation()}>
              <h4 className="font-semibold mb-3">{t("perf.new_followup")}</h4>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <select value={fu.type} onChange={(e) => setFU({ ...fu, type: e.target.value })} className="sw-input"><option value="30d">30d</option><option value="60d">60d</option><option value="90d">90d</option><option value="ad_hoc">Ad-hoc</option></select>
                <select value={fu.rating} onChange={(e) => setFU({ ...fu, rating: e.target.value })} className="sw-input"><option value="on_track">{t("status.on_track")}</option><option value="needs_improvement">{t("status.needs_improvement")}</option><option value="off_track">{t("status.off_track")}</option></select>
              </div>
              <textarea required rows={3} placeholder="Notes" value={fu.notes} onChange={(e) => setFU({ ...fu, notes: e.target.value })} className="sw-input mb-2" data-testid="fu-notes" />
              <input placeholder="Evidence URL (optional)" value={fu.evidence_url} onChange={(e) => setFU({ ...fu, evidence_url: e.target.value })} className="sw-input mb-3" />
              <div className="flex justify-end gap-2"><button onClick={() => setShowFU(false)} className="sw-btn-secondary">{t("common.cancel")}</button><button data-testid="save-fu-btn" onClick={addFU} disabled={working || !fu.notes} className="sw-btn-primary">{t("common.save")}</button></div>
            </div>
          </div>
        )}
        {showClose && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowClose(false)}>
            <div className="bg-white rounded-[16px] max-w-[480px] w-full p-5" onClick={(e) => e.stopPropagation()}>
              <h4 className="font-semibold mb-3">{t("perf.close_pip")}</h4>
              <select value={closeForm.outcome} onChange={(e) => setCloseForm({ ...closeForm, outcome: e.target.value })} className="sw-input mb-2">
                <option value="successful">Successful</option><option value="failed">{t("status.failed")}</option><option value="extended">Extended</option><option value="cancelled">Cancelled</option>
              </select>
              {closeForm.outcome === "extended" && <input type="date" placeholder={t("perf.new_end_date")} value={closeForm.new_end_date} onChange={(e) => setCloseForm({ ...closeForm, new_end_date: e.target.value })} className="sw-input mb-2" />}
              <textarea required rows={3} placeholder="Closing notes" value={closeForm.closing_notes} onChange={(e) => setCloseForm({ ...closeForm, closing_notes: e.target.value })} className="sw-input mb-3" data-testid="close-notes" />
              <div className="flex justify-end gap-2"><button onClick={() => setShowClose(false)} className="sw-btn-secondary">{t("common.cancel")}</button><button data-testid="confirm-close-pip-btn" onClick={closePIP} disabled={working || !closeForm.closing_notes} className="sw-btn-primary">{t("common.close")}</button></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Performance() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canConfigure = user?.role === "hr" || user?.role === "admin";
  const [tab, setTab] = useState("evaluations");
  const [counts, setCounts] = useState({ evals: 0, pips: 0 });
  useEffect(() => {
    Promise.all([api.get("/evaluations"), api.get("/pips")]).then(([e, p]) => setCounts({ evals: e.data.length, pips: p.data.length })).catch(() => {});
  }, []);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><TrendingUp size={22} /></div>
        <div>
          <h1 className="sw-heading text-[28px]">Performance</h1>
          <p className="text-[14px] text-[#747474]">{t("perf.subtitle")}</p>
        </div>
      </div>
      <div className="flex gap-2 border-b border-[#eeeded] pb-3">
        <Tab active={tab === "evaluations"} onClick={() => setTab("evaluations")} label="Evaluations" testid="tab-evaluations" count={counts.evals} />
        <Tab active={tab === "pips"} onClick={() => setTab("pips")} label="PIPs" testid="tab-pips" count={counts.pips} />
        {canConfigure && (
          <Tab active={tab === "config"} onClick={() => setTab("config")} label={t("perf.tab_config")} testid="tab-config" />
        )}
      </div>
      {tab === "evaluations" && <EvaluationsTab />}
      {tab === "pips" && <PIPTab />}
      {tab === "config" && canConfigure && <ConfigTab />}
    </div>
  );
}
